import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { ApiKeyView, MerchantView } from '@/api/types';
import { Card, ErrorNote, Money, Spinner, Timestamp } from '@/components/Common';
import { Modal } from '@/components/Modal';
import { useAuth } from '@/lib/auth';
import { money } from '@/lib/format';

export function MerchantsPage() {
  const { can } = useAuth();
  const [keysFor, setKeysFor] = useState<MerchantView | null>(null);
  const [editing, setEditing] = useState<MerchantView | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['merchants'],
    queryFn: () => api.get<{ merchants: MerchantView[] }>('/api/admin/merchants'),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;

  const merchants = data?.merchants ?? [];

  return (
    <div className="space-y-4">
      <Card
        title={`Merchants — ${merchants.length}`}
        actions={
          can('ADMIN') && (
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              Add merchant
            </button>
          )
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-ink-200">
              <tr>
                <th className="th">Code</th><th className="th">Name</th><th className="th">Contact</th>
                <th className="th">Type</th>
                <th className="th">Status</th>
                <th className="th text-right">Per txn</th><th className="th text-right">Daily</th>
                <th className="th text-right">Commission</th>
                <th className="th">Callback</th><th className="th">Float</th><th className="th" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {merchants.map((merchant) => (
                <tr key={merchant.id} className="hover:bg-ink-50">
                  <td className="td mono font-medium">{merchant.code}</td>
                  <td className="td">{merchant.name}</td>
                  <td className="td text-xs text-ink-600">{merchant.contactEmail ?? '—'}</td>
                  <td className="td">
                    {merchant.accountType === 'GENERAL' ? (
                      <span className="text-xs text-ink-400">—</span>
                    ) : (
                      <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[11px] font-semibold text-sky-800">
                        {merchant.accountType}
                      </span>
                    )}
                  </td>
                  <td className="td">
                    <span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${
                      merchant.status === 'ACTIVE'
                        ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
                    }`}>{merchant.status}</span>
                  </td>
                  <td className="td text-right">
                    <Money amount={merchant.perTxnLimit ? money(merchant.perTxnLimit) : null} />
                  </td>
                  <td className="td text-right">
                    <Money amount={merchant.dailyLimit ? money(merchant.dailyLimit) : null} />
                  </td>
                  <td className="td text-right">
                    {merchant.accountType === 'VENDOR' && merchant.commissionRate != null
                      ? `${(merchant.commissionRate * 100).toFixed(1)}%`
                      : <span className="text-xs text-ink-400">—</span>}
                  </td>
                  <td className="td max-w-xs truncate text-xs text-ink-600">
                    {merchant.callbackUrl ?? '—'}
                    {merchant.callbackUrl && !merchant.callbackSecretSet && (
                      <span className="ml-1 text-amber-700" title="Deliveries will be unsigned">
                        (unsigned)
                      </span>
                    )}
                  </td>
                  <td className="td text-xs">{merchant.viewFloat ? 'VIEW_FLOAT' : '—'}</td>
                  <td className="td text-right">
                    <div className="flex justify-end gap-2">
                      {can('ADMIN', 'OPS') && (
                        <button type="button" className="btn-secondary"
                                onClick={() => setKeysFor(merchant)}>Keys</button>
                      )}
                      {can('ADMIN') && (
                        <button type="button" className="btn-secondary"
                                onClick={() => setEditing(merchant)}>Edit</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {keysFor && <ApiKeysModal merchant={keysFor} onClose={() => setKeysFor(null)} />}
      {editing && <EditMerchantModal merchant={editing} onClose={() => setEditing(null)} />}
      {creating && <CreateMerchantModal onClose={() => setCreating(false)} />}
    </div>
  );
}

function CreateMerchantModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: '', name: '', contactEmail: '', accountType: 'GENERAL' as MerchantView['accountType'],
  });
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contactEmail);

  const create = useMutation({
    mutationFn: () => api.post<MerchantView>('/api/admin/merchants', {
      code: form.code,
      name: form.name,
      contactEmail: form.contactEmail,
      accountType: form.accountType,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
      onClose();
    },
  });

  return (
    <Modal title="Add a merchant" onClose={onClose}>
      <div className="space-y-3">
        <label className="block">
          <span className="label">Code</span>
          <input className="input mt-1 mono" value={form.code} placeholder="HRE-CBD01"
                 onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          <span className="mt-1 block text-xs text-ink-500">
            Short and stable — it feeds every agent reference this merchant's orders get.
          </span>
        </label>
        <label className="block">
          <span className="label">Name</span>
          <input className="input mt-1" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="block">
          <span className="label">Contact email</span>
          <input className="input mt-1" type="email" value={form.contactEmail}
                 placeholder="billing@merchant.com"
                 onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
          <span className="mt-1 block text-xs text-ink-500">
            The merchant's own address — not yours. Used to reach them about their account.
          </span>
        </label>
        <label className="block">
          <span className="label">Account type</span>
          <select className="input mt-1" value={form.accountType}
                  onChange={(e) => setForm({
                    ...form, accountType: e.target.value as MerchantView['accountType'],
                  })}>
            <option value="GENERAL">General (API-integrated merchant)</option>
            <option value="VENDOR">Vendor (click-through selling screen, cash or EcoCash)</option>
            <option value="COMPANY">Company (pays for its own recharges in the workspace)</option>
          </select>
        </label>
      </div>

      {create.isError && <ErrorNote error={create.error} />}

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary"
                disabled={create.isPending || !form.code || !form.name || !emailValid}
                onClick={() => create.mutate()}>
          {create.isPending ? 'Creating…' : 'Create'}
        </button>
      </div>
    </Modal>
  );
}

function ApiKeysModal({ merchant, onClose }: { merchant: MerchantView; onClose: () => void }) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [label, setLabel] = useState('');
  const [issued, setIssued] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['api-keys', merchant.id],
    queryFn: () => api.get<{ apiKeys: ApiKeyView[] }>(`/api/admin/merchants/${merchant.id}/api-keys`),
  });

  const issue = useMutation({
    mutationFn: () => api.post<{ apiKey: string }>(
      `/api/admin/merchants/${merchant.id}/api-keys?label=${encodeURIComponent(label)}`),
    onSuccess: (result) => {
      setIssued(result.apiKey);
      queryClient.invalidateQueries({ queryKey: ['api-keys', merchant.id] });
    },
  });

  const revoke = useMutation({
    mutationFn: (keyId: string) => api.delete(`/api/admin/merchants/api-keys/${keyId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['api-keys', merchant.id] }),
  });

  return (
    <Modal title={`API keys — ${merchant.code}`} onClose={onClose}>
      {issued && (
        <div className="mb-4 rounded border border-amber-300 bg-amber-50 p-3">
          <p className="text-xs font-semibold text-amber-900">
            Copy this now. Only a BCrypt hash is stored, so it cannot be shown again.
          </p>
          <code className="mt-2 block break-all rounded bg-white px-2 py-1.5 font-mono text-xs">
            {issued}
          </code>
        </div>
      )}

      <ul className="divide-y divide-ink-100">
        {(data?.apiKeys ?? []).map((key) => (
          <li key={key.id} className="flex items-center justify-between py-2 text-sm">
            <div>
              <span className="mono">{key.keyPrefix}…</span>
              {key.label && <span className="ml-2 text-ink-600">{key.label}</span>}
              <div className="text-xs text-ink-500">
                {key.revokedAt
                  ? <span className="text-red-700">revoked <Timestamp value={key.revokedAt} /></span>
                  : <>last used <Timestamp value={key.lastUsedAt} /></>}
              </div>
            </div>
            {!key.revokedAt && can('ADMIN') && (
              <button type="button" className="btn-secondary text-red-700"
                      onClick={() => revoke.mutate(key.id)}>Revoke</button>
            )}
          </li>
        ))}
        {(data?.apiKeys ?? []).length === 0 && (
          <li className="py-4 text-sm text-ink-500">No keys issued.</li>
        )}
      </ul>

      {can('ADMIN') && (
        <div className="mt-4 flex items-end gap-2 border-t border-ink-200 pt-4">
          <label className="flex-1">
            <span className="label">Label</span>
            <input className="input mt-1" value={label} placeholder="Till 01 POS"
                   onChange={(e) => setLabel(e.target.value)} />
          </label>
          <button type="button" className="btn-primary" disabled={issue.isPending}
                  onClick={() => issue.mutate()}>
            {issue.isPending ? 'Issuing…' : 'Issue key'}
          </button>
        </div>
      )}
      {issue.isError && <ErrorNote error={issue.error} />}
    </Modal>
  );
}

function EditMerchantModal({ merchant, onClose }: { merchant: MerchantView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    name: merchant.name,
    contactEmail: merchant.contactEmail ?? '',
    status: merchant.status,
    accountType: merchant.accountType,
    callbackUrl: merchant.callbackUrl ?? '',
    callbackSecret: '',
    perTxnLimit: merchant.perTxnLimit ?? '',
    dailyLimit: merchant.dailyLimit ?? '',
    viewFloat: merchant.viewFloat,
    // Stored as a fraction (0.10 = 10%); edited here as a whole percentage for readability.
    commissionPercent: merchant.commissionRate != null ? String(merchant.commissionRate * 100) : '',
  });

  const save = useMutation({
    mutationFn: () => api.patch(`/api/admin/merchants/${merchant.id}`, {
      name: form.name,
      contactEmail: form.contactEmail || null,
      status: form.status,
      accountType: form.accountType,
      callbackUrl: form.callbackUrl || null,
      callbackSecret: form.callbackSecret || null,
      perTxnLimit: form.perTxnLimit || null,
      dailyLimit: form.dailyLimit || null,
      viewFloat: form.viewFloat,
      commissionRate: form.commissionPercent
        ? (Number(form.commissionPercent) / 100).toString() : null,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['merchants'] });
      onClose();
    },
  });

  return (
    <Modal title={`Edit ${merchant.code}`} onClose={onClose}>
      <div className="space-y-3">
        <label className="block">
          <span className="label">Name</span>
          <input className="input mt-1" value={form.name}
                 onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label className="block">
          <span className="label">Contact email</span>
          <input className="input mt-1" type="email" value={form.contactEmail}
                 placeholder="billing@merchant.com"
                 onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
        </label>
        <label className="block">
          <span className="label">Status</span>
          <select className="input mt-1" value={form.status}
                  onChange={(e) => setForm({
                    ...form, status: e.target.value as MerchantView['status'],
                  })}>
            <option value="ACTIVE">ACTIVE</option>
            <option value="SUSPENDED">SUSPENDED</option>
          </select>
        </label>
        <label className="block">
          <span className="label">Account type</span>
          <select className="input mt-1" value={form.accountType}
                  onChange={(e) => setForm({
                    ...form, accountType: e.target.value as MerchantView['accountType'],
                  })}>
            <option value="GENERAL">General (API-integrated merchant)</option>
            <option value="VENDOR">Vendor (click-through selling screen, cash or EcoCash)</option>
            <option value="COMPANY">Company (pays for its own recharges in the workspace)</option>
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label>
            <span className="label">Per transaction limit</span>
            <input className="input mt-1 tabular" value={form.perTxnLimit} inputMode="decimal"
                   onChange={(e) => setForm({ ...form, perTxnLimit: e.target.value })} />
          </label>
          <label>
            <span className="label">Daily limit</span>
            <input className="input mt-1 tabular" value={form.dailyLimit} inputMode="decimal"
                   onChange={(e) => setForm({ ...form, dailyLimit: e.target.value })} />
          </label>
        </div>
        <label className="block">
          <span className="label">Callback URL</span>
          <input className="input mt-1" value={form.callbackUrl} placeholder="https://…"
                 onChange={(e) => setForm({ ...form, callbackUrl: e.target.value })} />
        </label>
        <label className="block">
          <span className="label">Callback secret</span>
          <input className="input mt-1" type="password" value={form.callbackSecret}
                 placeholder={merchant.callbackSecretSet ? 'Set — leave blank to keep' : 'Not set'}
                 onChange={(e) => setForm({ ...form, callbackSecret: e.target.value })} />
          <span className="mt-1 block text-xs text-ink-500">
            Signs webhook deliveries as {'X-Signature: t=…,v1=…'}. Stored encrypted and never
            shown again.
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={form.viewFloat}
                 onChange={(e) => setForm({ ...form, viewFloat: e.target.checked })} />
          Grant VIEW_FLOAT
        </label>
        {form.accountType === 'VENDOR' && (
          <label className="block">
            <span className="label">Commission (%)</span>
            <input className="input mt-1 tabular" value={form.commissionPercent} inputMode="decimal"
                   placeholder="10"
                   onChange={(e) => setForm({ ...form, commissionPercent: e.target.value })} />
            <span className="mt-1 block text-xs text-ink-500">
              Paid on the USD-equivalent of every sale this vendor makes — see Vendor payouts.
            </span>
          </label>
        )}
      </div>

      {save.isError && <ErrorNote error={save.error} />}

      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" disabled={save.isPending}
                onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}
